import { Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { HiOutlineIdentification, HiOutlineBanknotes, HiOutlineGift, HiOutlineClock } from "react-icons/hi2";
import {
    Field,
    FormInput,
    FormTextarea,
    FormSelect,
    FormSwitch,
    SwitchGroup,
    Grid2,
    Grid3,
    CollapsibleSection,
    ItemCard,
    ItemHeader,
    AddButton,
    Divider,
    SubsectionTitle,
    WeightField,
    AdditionalInfoField,
    isFieldInvalid,
    isFieldWarning,
} from "../ui";
import {
    useJobOfferEnums,
    createEmptyEmploymentForm,
    createEmptySalaryModel,
    createEmptyBonus,
    createEmptyAllowance,
    createEmptySalaryModelHourly,
    createEmptySalaryModelFixed,
    createEmptySalaryModelPieceRate,
    createEmptySalaryModelB2B,
} from "@/models/jobOffer";
import {useCallback} from "react";
import { ScoringConfigPanel, SCORING_ENABLED } from "@/scoringUi";
import { buildScoringLabels } from "../scoringLabels";

const Step2Employment = ({ formik, warningErrors, visited, forceOpen }) => {
    const { t } = useTranslation("jobOffer");
    const {
        EMPLOYMENT_FORM_TYPE,
        SALARY_MODEL_TYPE,
        CURRENCY,
        PIECE_RATE_UNIT,
        SETTLEMENT_PERIOD,
        BONUS_TYPE,
        ALLOWANCE_TYPE,
        WORKLOAD_TYPE,
        WORK_SYSTEM,
        OVERTIME_FREQUENCY,
        OVERTIME_SETTLEMENT,
    } = useJobOfferEnums();
    const { values, setFieldValue } = formik;
    const invalid = (path) => isFieldInvalid(formik, path);
    const warn = (path) => isFieldWarning(warningErrors, visited, path);
    const { employmentForms, salaryModels, bonuses, allowances, workingTime } = values;
    const scoringLabels = buildScoringLabels(t);
    const sectionInfo = values.sectionAdditionalInfo ?? {};
    const setSectionInfo = (key, val) => setFieldValue(`sectionAdditionalInfo.${key}`, val);

    const updateEmploymentForm = (i, updates) =>
        setFieldValue("employmentForms", employmentForms.map((f, idx) => (idx === i ? { ...f, ...updates } : f)));
    const removeEmploymentForm = (i) => setFieldValue("employmentForms", employmentForms.filter((_, idx) => idx !== i));

    const updateSalaryModel = (i, updates) =>
        setFieldValue("salaryModels", salaryModels.map((m, idx) => (idx === i ? { ...m, ...updates } : m)));
    const removeSalaryModel = (i) => setFieldValue("salaryModels", salaryModels.filter((_, idx) => idx !== i));

    const onSalaryTypeChange = (i, salaryModel, type) => {
        const updates = { type };
        if (type === "hourlyRate" && !salaryModel.hourly) updates.hourly = createEmptySalaryModelHourly();
        if (type === "fixedMonthly" && !salaryModel.fixedSalary) updates.fixedSalary = createEmptySalaryModelFixed();
        if ((type === "pieceRate" || type === "pieceRatePlusBase") && !salaryModel.pieceRate) updates.pieceRate = createEmptySalaryModelPieceRate();
        if (type === "b2b" && !salaryModel.b2b) updates.b2b = createEmptySalaryModelB2B();
        updateSalaryModel(i, updates);
    };

    const updateBonus = (i, updates) =>
        setFieldValue("bonuses", bonuses.map((b, idx) => (idx === i ? { ...b, ...updates } : b)));
    const removeBonus = (i) => setFieldValue("bonuses", bonuses.filter((_, idx) => idx !== i));

    const updateAllowance = (i, updates) =>
        setFieldValue("allowances", allowances.map((a, idx) => (idx === i ? { ...a, ...updates } : a)));
    const removeAllowance = (i) => setFieldValue("allowances", allowances.filter((_, idx) => idx !== i));

    const setWorkingTime = (updates) => setFieldValue("workingTime", { ...workingTime, ...updates });
    const setOvertime = (updates) => setFieldValue("workingTime.overtime", { ...workingTime.overtime, ...updates });


    return (
        <Box display="flex" flexDirection="column" gap={6}>
            {/* Section 3: Employment form */}
            <CollapsibleSection
                number={3}
                title={t("step2.employmentForm.title")}
                subtitle={t("step2.employmentForm.subtitle")}
                icon={HiOutlineIdentification}
                defaultOpen
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={3}>
                    {employmentForms.map((form, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step2.employmentForm.itemTitle")} index={i} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Grid2>
                                    <Field label={t("step2.employmentForm.type")} required invalid={invalid(`employmentForms.${i}.type`)} warning={warn(`employmentForms.${i}.type`)}>
                                        <FormSelect
                                            value={form.type}
                                            onChange={(v) => updateEmploymentForm(i, { type: v })}
                                            placeholder={t("step2.employmentForm.typePlaceholder")}
                                            options={EMPLOYMENT_FORM_TYPE}
                                            multiple
                                        />
                                    </Field>
                                    <Field label={t("step2.employmentForm.preferredType")} required invalid={invalid(`employmentForms.${i}.preferredType`)} warning={warn(`employmentForms.${i}.preferredType`)}>
                                        <FormSelect
                                            value={form.preferredType}
                                            onChange={(v) => updateEmploymentForm(i, { preferredType: v })}
                                            placeholder={t("step2.employmentForm.preferredTypePlaceholder")}
                                            options={EMPLOYMENT_FORM_TYPE}
                                        />
                                    </Field>
                                </Grid2>
                                <Grid2>
                                    {false && (
                                        <WeightField
                                            value={form.weight}
                                            onChange={(v) => updateEmploymentForm(i, { weight: v })}
                                        />
                                    )}
                                    <Field label={t("step2.employmentForm.aiQuestion")} required invalid={invalid(`employmentForms.${i}.aiQuestion`)} warning={warn(`employmentForms.${i}.aiQuestion`)}>
                                        <FormInput
                                            value={form.aiQuestion}
                                            onChange={(e) => updateEmploymentForm(i, { aiQuestion: e.target.value })}
                                            placeholder={t("step2.employmentForm.aiQuestionPlaceholder")}
                                        />
                                    </Field>
                                </Grid2>
                                <FormSwitch
                                    label={t("step2.employmentForm.eliminating")}
                                    checked={form.isEliminatingIfNotAccepted}
                                    onChange={(v) => updateEmploymentForm(i, { isEliminatingIfNotAccepted: v })}
                                />
                                {SCORING_ENABLED && (
                                    <>
                                        <Divider my={1} />
                                        <ScoringConfigPanel
                                            config={form.scoring}
                                            onChange={(scoring) => updateEmploymentForm(i, { scoring })}
                                            labels={scoringLabels}
                                        />
                                    </>
                                )}
                            </Box>
                        </ItemCard>
                    ))}
                    <AdditionalInfoField value={sectionInfo.employmentForms} onChange={(e) => setSectionInfo("employmentForms", e.target.value)} />
                </Box>
            </CollapsibleSection>

            {/* Section 4: Salary model */}
            <CollapsibleSection
                number={4}
                title={t("step2.salaryModel.title")}
                subtitle={t("step2.salaryModel.subtitle")}
                icon={HiOutlineBanknotes}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={3}>
                    {salaryModels.map((salaryModel, i) => {
                        const setSalaryModel = (updates) => updateSalaryModel(i, updates);
                        return (
                            <ItemCard key={i}>
                                <ItemHeader title={t("step2.salaryModel.itemTitle")} index={i} onRemove={() => removeSalaryModel(i)} />
                                <Box display="flex" flexDirection="column" gap={3}>
                                    <Grid3>
                                        <Field label={t("step2.salaryModel.type")} required invalid={invalid(`salaryModels.${i}.type`)} warning={warn(`salaryModels.${i}.type`)}>
                                            <FormSelect value={salaryModel.type} onChange={(v) => onSalaryTypeChange(i, salaryModel, v)} placeholder={t("step2.salaryModel.typePlaceholder")} options={SALARY_MODEL_TYPE} />
                                        </Field>
                                        <Field label={t("step2.salaryModel.currency")} required invalid={invalid(`salaryModels.${i}.currency`)} warning={warn(`salaryModels.${i}.currency`)}>
                                            <FormSelect value={salaryModel.currency} onChange={(v) => setSalaryModel({ currency: v })} options={CURRENCY} />
                                        </Field>
                                    </Grid3>

                                    <SwitchGroup>
                                        <FormSwitch label={t("step2.salaryModel.hasBonus")} checked={salaryModel.hasBonus} onChange={(v) => setSalaryModel({ hasBonus: v })} />
                                        <FormSwitch label={t("step2.salaryModel.isNegotiable")} checked={salaryModel.isNegotiable} onChange={(v) => setSalaryModel({ isNegotiable: v })} />
                                        <FormSwitch label={t("step2.salaryModel.canCommunicateToCandidate")} checked={salaryModel.canCommunicateToCandidate} onChange={(v) => setSalaryModel({ canCommunicateToCandidate: v })} />
                                    </SwitchGroup>

                                    {salaryModel.type === "hourlyRate" && salaryModel.hourly && (
                                        <>
                                            <Divider my={2} />
                                            <SubsectionTitle>{t("step2.salaryModel.hourly.title")}</SubsectionTitle>
                                            <Grid2>
                                                <Field label={t("step2.salaryModel.hourly.rateFrom")}>
                                                    <FormInput type="number" value={salaryModel.hourly.rateFrom ?? ""} onChange={(e) => setSalaryModel({ hourly: { ...salaryModel.hourly, rateFrom: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.hourly.rateFromPlaceholder")} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.hourly.rateTo")}>
                                                    <FormInput type="number" value={salaryModel.hourly.rateTo ?? ""} onChange={(e) => setSalaryModel({ hourly: { ...salaryModel.hourly, rateTo: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.hourly.rateToPlaceholder")} />
                                                </Field>
                                            </Grid2>
                                            <FormSwitch
                                                label={t("step2.salaryModel.hourly.hasDifferentOvertimeRate")}
                                                checked={salaryModel.hourly.hasDifferentOvertimeRate}
                                                onChange={(v) => setSalaryModel({ hourly: { ...salaryModel.hourly, hasDifferentOvertimeRate: v } })}
                                            />
                                            {salaryModel.hourly.hasDifferentOvertimeRate && (
                                                <Field label={t("step2.salaryModel.hourly.overtimeDescription")}>
                                                    <FormTextarea
                                                        value={salaryModel.hourly.overtimeDescription}
                                                        onChange={(e) => setSalaryModel({ hourly: { ...salaryModel.hourly, overtimeDescription: e.target.value } })}
                                                        placeholder={t("step2.salaryModel.hourly.overtimeDescriptionPlaceholder")}
                                                        rows={2}
                                                    />
                                                </Field>
                                            )}
                                        </>
                                    )}

                                    {salaryModel.type === "fixedMonthly" && salaryModel.fixedSalary && (
                                        <>
                                            <Divider my={2} />
                                            <SubsectionTitle>{t("step2.salaryModel.fixed.title")}</SubsectionTitle>
                                            <Grid2>
                                                <Field label={t("step2.salaryModel.fixed.grossFrom")}>
                                                    <FormInput type="number" value={salaryModel.fixedSalary.grossMonthlyFrom ?? ""} onChange={(e) => setSalaryModel({ fixedSalary: { ...salaryModel.fixedSalary, grossMonthlyFrom: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.fixed.grossFromPlaceholder")} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.fixed.grossTo")}>
                                                    <FormInput type="number" value={salaryModel.fixedSalary.grossMonthlyTo ?? ""} onChange={(e) => setSalaryModel({ fixedSalary: { ...salaryModel.fixedSalary, grossMonthlyTo: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.fixed.grossToPlaceholder")} />
                                                </Field>
                                            </Grid2>
                                            <Field label={t("step2.salaryModel.fixed.isFixedDescription")}>
                                                <FormInput
                                                    value={salaryModel.fixedSalary.isFixedDescription}
                                                    onChange={(e) => setSalaryModel({ fixedSalary: { ...salaryModel.fixedSalary, isFixedDescription: e.target.value } })}
                                                    placeholder={t("step2.salaryModel.fixed.isFixedDescriptionPlaceholder")}
                                                />
                                            </Field>
                                        </>
                                    )}

                                    {(salaryModel.type === "pieceRate" || salaryModel.type === "pieceRatePlusBase") && salaryModel.pieceRate && (
                                        <>
                                            <Divider my={2} />
                                            <SubsectionTitle>{t("step2.salaryModel.pieceRate.title")}</SubsectionTitle>
                                            <Grid3>
                                                <Field label={t("step2.salaryModel.pieceRate.unit")}>
                                                    <FormSelect value={salaryModel.pieceRate.unit} onChange={(v) => setSalaryModel({ pieceRate: { ...salaryModel.pieceRate, unit: v } })} placeholder={t("step2.salaryModel.pieceRate.unitPlaceholder")} options={PIECE_RATE_UNIT} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.pieceRate.pricePerUnit")}>
                                                    <FormInput type="number" value={salaryModel.pieceRate.pricePerUnit ?? ""} onChange={(e) => setSalaryModel({ pieceRate: { ...salaryModel.pieceRate, pricePerUnit: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.pieceRate.pricePerUnitPlaceholder")} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.pieceRate.avgUnits")}>
                                                    <FormInput value={salaryModel.pieceRate.avgUnitsDescription} onChange={(e) => setSalaryModel({ pieceRate: { ...salaryModel.pieceRate, avgUnitsDescription: e.target.value } })} placeholder={t("step2.salaryModel.pieceRate.avgUnitsPlaceholder")} />
                                                </Field>
                                            </Grid3>
                                            <Field label={t("step2.salaryModel.pieceRate.calculationDescription")}>
                                                <FormTextarea value={salaryModel.pieceRate.calculationDescription} onChange={(e) => setSalaryModel({ pieceRate: { ...salaryModel.pieceRate, calculationDescription: e.target.value } })} rows={2} placeholder={t("step2.salaryModel.pieceRate.calculationDescriptionPlaceholder")} />
                                            </Field>
                                            <Grid3>
                                                <Field label={t("step2.salaryModel.pieceRate.avgMonthly")}>
                                                    <FormInput type="number" value={salaryModel.pieceRate.avgMonthlySalary ?? ""} onChange={(e) => setSalaryModel({ pieceRate: { ...salaryModel.pieceRate, avgMonthlySalary: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.pieceRate.avgMonthlyPlaceholder")} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.pieceRate.minSalary")}>
                                                    <FormInput type="number" value={salaryModel.pieceRate.minSalary ?? ""} onChange={(e) => setSalaryModel({ pieceRate: { ...salaryModel.pieceRate, minSalary: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.pieceRate.minSalaryPlaceholder")} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.pieceRate.maxSalary")}>
                                                    <FormInput type="number" value={salaryModel.pieceRate.maxSalary ?? ""} onChange={(e) => setSalaryModel({ pieceRate: { ...salaryModel.pieceRate, maxSalary: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.pieceRate.maxSalaryPlaceholder")} />
                                                </Field>
                                            </Grid3>
                                        </>
                                    )}

                                    {salaryModel.type === "b2b" && salaryModel.b2b && (
                                        <>
                                            <Divider my={2} />
                                            <SubsectionTitle>{t("step2.salaryModel.b2b.title")}</SubsectionTitle>
                                            <Grid2>
                                                <Field label={t("step2.salaryModel.b2b.netFrom")}>
                                                    <FormInput type="number" value={salaryModel.b2b.netInvoiceAmountFrom ?? ""} onChange={(e) => setSalaryModel({ b2b: { ...salaryModel.b2b, netInvoiceAmountFrom: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.b2b.netFromPlaceholder")} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.b2b.netTo")}>
                                                    <FormInput type="number" value={salaryModel.b2b.netInvoiceAmountTo ?? ""} onChange={(e) => setSalaryModel({ b2b: { ...salaryModel.b2b, netInvoiceAmountTo: e.target.value === "" ? null : Number(e.target.value) } })} placeholder={t("step2.salaryModel.b2b.netToPlaceholder")} />
                                                </Field>
                                            </Grid2>
                                            <Grid2>
                                                <Field label={t("step2.salaryModel.b2b.settlementPeriod")}>
                                                    <FormSelect value={salaryModel.b2b.settlementPeriod} onChange={(v) => setSalaryModel({ b2b: { ...salaryModel.b2b, settlementPeriod: v } })} placeholder={t("step2.salaryModel.b2b.settlementPeriodPlaceholder")} options={SETTLEMENT_PERIOD} />
                                                </Field>
                                                <Field label={t("step2.salaryModel.b2b.paymentTerm")}>
                                                    <FormInput value={salaryModel.b2b.paymentTerm} onChange={(e) => setSalaryModel({ b2b: { ...salaryModel.b2b, paymentTerm: e.target.value } })} placeholder={t("step2.salaryModel.b2b.paymentTermPlaceholder")} />
                                                </Field>
                                            </Grid2>
                                        </>
                                    )}
                                </Box>
                            </ItemCard>
                        );
                    })}
                    <AddButton label={t("step2.salaryModel.add")} onClick={() => setFieldValue("salaryModels", [...salaryModels, createEmptySalaryModel()])} />
                    <AdditionalInfoField value={sectionInfo.salaryModels} onChange={(e) => setSectionInfo("salaryModels", e.target.value)} />
                </Box>
            </CollapsibleSection>

            {/* Section 5: Bonuses + Allowances */}
            <CollapsibleSection
                number={5}
                title={t("step2.bonusesAllowances.title")}
                subtitle={t("step2.bonusesAllowances.subtitle")}
                icon={HiOutlineGift}
                forceOpen={forceOpen}
            >
                <SubsectionTitle>{t("step2.bonusesAllowances.bonusesTitle")}</SubsectionTitle>
                <Box display="flex" flexDirection="column" gap={3} mb={5}>
                    {bonuses.map((bonus, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step2.bonusesAllowances.bonusItemTitle")} index={i} onRemove={() => removeBonus(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Grid2>
                                    <Field label={t("step2.bonusesAllowances.bonusName")} required invalid={invalid(`bonuses.${i}.name`)} warning={warn(`bonuses.${i}.name`)}>
                                        <FormInput value={bonus.name} onChange={(e) => updateBonus(i, { name: e.target.value })} placeholder={t("step2.bonusesAllowances.bonusNamePlaceholder")} />
                                    </Field>
                                    <Field label={t("step2.bonusesAllowances.bonusType")} required invalid={invalid(`bonuses.${i}.type`)} warning={warn(`bonuses.${i}.type`)}>
                                        <FormSelect value={bonus.type} onChange={(v) => updateBonus(i, { type: v })} placeholder={t("step2.bonusesAllowances.bonusTypePlaceholder")} options={BONUS_TYPE} />
                                    </Field>
                                </Grid2>
                                <Field label={t("step2.bonusesAllowances.calculationMethod")}>
                                    <FormTextarea value={bonus.calculationDescription} onChange={(e) => updateBonus(i, { calculationDescription: e.target.value })} placeholder={t("step2.bonusesAllowances.bonusCalculationPlaceholder")} rows={2} />
                                </Field>
                                <Grid2>
                                    <Field label={t("step2.bonusesAllowances.amountFrom")}>
                                        <FormInput type="number" value={bonus.amountFrom ?? ""} onChange={(e) => updateBonus(i, { amountFrom: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step2.bonusesAllowances.bonusAmountFromPlaceholder")} />
                                    </Field>
                                    <Field label={t("step2.bonusesAllowances.amountTo")}>
                                        <FormInput type="number" value={bonus.amountTo ?? ""} onChange={(e) => updateBonus(i, { amountTo: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step2.bonusesAllowances.bonusAmountToPlaceholder")} />
                                    </Field>
                                </Grid2>
                                <SwitchGroup>
                                    <FormSwitch label={t("step2.bonusesAllowances.guaranteed")} checked={bonus.isGuaranteed} onChange={(v) => updateBonus(i, { isGuaranteed: v })} />
                                    <FormSwitch label={t("step2.salaryModel.canCommunicateToCandidate")} checked={bonus.canCommunicateToCandidate} onChange={(v) => updateBonus(i, { canCommunicateToCandidate: v })} />
                                </SwitchGroup>
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step2.bonusesAllowances.addBonus")} onClick={() => setFieldValue("bonuses", [...bonuses, createEmptyBonus()])} />
                </Box>

                <Divider />

                <SubsectionTitle>{t("step2.bonusesAllowances.allowancesTitle")}</SubsectionTitle>
                <Box display="flex" flexDirection="column" gap={3}>
                    {allowances.map((allowance, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step2.bonusesAllowances.allowanceItemTitle")} index={i} onRemove={() => removeAllowance(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Grid2>
                                    <Field label={t("step2.bonusesAllowances.allowanceName")} required invalid={invalid(`allowances.${i}.name`)} warning={warn(`allowances.${i}.name`)}>
                                        <FormInput value={allowance.name} onChange={(e) => updateAllowance(i, { name: e.target.value })} placeholder={t("step2.bonusesAllowances.allowanceNamePlaceholder")} />
                                    </Field>
                                    <Field label={t("step2.bonusesAllowances.allowanceType")} required invalid={invalid(`allowances.${i}.type`)} warning={warn(`allowances.${i}.type`)}>
                                        <FormSelect value={allowance.type} onChange={(v) => updateAllowance(i, { type: v })} placeholder={t("step2.bonusesAllowances.allowanceTypePlaceholder")} options={ALLOWANCE_TYPE} />
                                    </Field>
                                </Grid2>
                                <Field label={t("step2.bonusesAllowances.calculationMethod")}>
                                    <FormTextarea value={allowance.calculationDescription} onChange={(e) => updateAllowance(i, { calculationDescription: e.target.value })} placeholder={t("step2.bonusesAllowances.allowanceCalculationPlaceholder")} rows={2} />
                                </Field>
                                <Grid2>
                                    <Field label={t("step2.bonusesAllowances.amountFrom")}>
                                        <FormInput type="number" value={allowance.amountFrom ?? ""} onChange={(e) => updateAllowance(i, { amountFrom: e.target.value === "" ? null : Number(e.target.value) })} />
                                    </Field>
                                    <Field label={t("step2.bonusesAllowances.amountTo")}>
                                        <FormInput type="number" value={allowance.amountTo ?? ""} onChange={(e) => updateAllowance(i, { amountTo: e.target.value === "" ? null : Number(e.target.value) })} />
                                    </Field>
                                </Grid2>
                                <SwitchGroup>
                                    <FormSwitch label={t("step2.salaryModel.canCommunicateToCandidate")} checked={allowance.canCommunicateToCandidate} onChange={(v) => updateAllowance(i, { canCommunicateToCandidate: v })} />
                                </SwitchGroup>
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step2.bonusesAllowances.addAllowance")} onClick={() => setFieldValue("allowances", [...allowances, createEmptyAllowance()])} />
                </Box>
                <Box mt={4}>
                    <AdditionalInfoField value={sectionInfo.bonusesAndAllowances} onChange={(e) => setSectionInfo("bonusesAndAllowances", e.target.value)} />
                </Box>
            </CollapsibleSection>

            {/* Section 6: Working time */}
            <CollapsibleSection
                number={6}
                title={t("step2.workingTime.title")}
                subtitle={t("step2.workingTime.subtitle")}
                icon={HiOutlineClock}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={4}>
                    <Grid2>
                        <Field label={t("step2.workingTime.workloadType")} required invalid={invalid("workingTime.workloadType")} warning={warn("workingTime.workloadType")}>
                            <FormSelect value={workingTime.workloadType} onChange={(v) => setWorkingTime({ workloadType: v })} placeholder={t("step2.workingTime.workloadTypePlaceholder")} options={WORKLOAD_TYPE} />
                        </Field>
                        <Field label={t("step2.workingTime.workSystem")} required invalid={invalid("workingTime.workSystem")} warning={warn("workingTime.workSystem")}>
                            <FormSelect value={workingTime.workSystem} onChange={(v) => setWorkingTime({ workSystem: v })} placeholder={t("step2.workingTime.workSystemPlaceholder")} options={WORK_SYSTEM} />
                        </Field>
                    </Grid2>

                    <Grid3>
                        <Field label={t("step2.workingTime.minHoursPerWeek")}>
                            <FormInput type="number" value={workingTime.minHoursPerWeek ?? ""} onChange={(e) => setWorkingTime({ minHoursPerWeek: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step2.workingTime.minHoursPlaceholder")} />
                        </Field>
                        <Field label={t("step2.workingTime.maxHoursPerWeek")}>
                            <FormInput type="number" value={workingTime.maxHoursPerWeek ?? ""} onChange={(e) => setWorkingTime({ maxHoursPerWeek: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step2.workingTime.maxHoursPlaceholder")} />
                        </Field>
                        <Field label={t("step2.workingTime.startTime")}>
                            <FormInput type="time" value={workingTime.startTime} onChange={(e) => setWorkingTime({ startTime: e.target.value })} />
                        </Field>
                    </Grid3>

                    <Grid3>
                        <Box />
                        <Box />
                        <Field label={t("step2.workingTime.endTime")}>
                            <FormInput type="time" value={workingTime.endTime} onChange={(e) => setWorkingTime({ endTime: e.target.value })} />
                        </Field>
                    </Grid3>

                    <SwitchGroup mt={1}>
                        <FormSwitch label={t("step2.workingTime.fixedSchedule")} checked={workingTime.isFixedSchedule} onChange={(v) => setWorkingTime({ isFixedSchedule: v })} />
                        <FormSwitch label={t("step2.workingTime.flexibleSchedule")} checked={workingTime.isFlexibleSchedule} onChange={(v) => setWorkingTime({ isFlexibleSchedule: v })} />
                        <FormSwitch label={t("step2.workingTime.worksWeekends")} checked={workingTime.worksWeekends} onChange={(v) => setWorkingTime({ worksWeekends: v })} />
                        <FormSwitch label={t("step2.workingTime.worksHolidays")} checked={workingTime.worksHolidays} onChange={(v) => setWorkingTime({ worksHolidays: v })} />
                        <FormSwitch label={t("step2.workingTime.hasBusinessTrips")} checked={workingTime.hasBusinessTrips} onChange={(v) => setWorkingTime({ hasBusinessTrips: v })} />
                        <FormSwitch label={t("step2.workingTime.worksNightShifts")} checked={workingTime.worksNightShifts} onChange={(v) => setWorkingTime({ worksNightShifts: v })} />
                        <FormSwitch label={t("step2.workingTime.hasOnCallDuty")} checked={workingTime.hasOnCallDuty} onChange={(v) => setWorkingTime({ hasOnCallDuty: v })} />
                    </SwitchGroup>

                    <Divider />

                    <SubsectionTitle>{t("step2.workingTime.overtimeTitle")}</SubsectionTitle>
                    <FormSwitch label={t("step2.workingTime.overtimeExists")} checked={workingTime.overtime.exists} onChange={(v) => setOvertime({ exists: v })} />

                    {workingTime.overtime.exists && (
                        <Box display="flex" flexDirection="column" gap={3} pl={2} borderLeft="2px solid" borderColor="purple.900">
                            <Grid2>
                                <Field label={t("step2.workingTime.overtimeFrequency")} required invalid={invalid("workingTime.overtime.frequency")} warning={warn("workingTime.overtime.frequency")}>
                                    <FormSelect value={workingTime.overtime.frequency} onChange={(v) => setOvertime({ frequency: v })} placeholder={t("step2.workingTime.overtimeFrequencyPlaceholder")} options={OVERTIME_FREQUENCY} />
                                </Field>
                                <Field label={t("step2.workingTime.overtimeSettlementMethod")} required invalid={invalid("workingTime.overtime.settlementMethod")} warning={warn("workingTime.overtime.settlementMethod")}>
                                    <FormSelect value={workingTime.overtime.settlementMethod} onChange={(v) => setOvertime({ settlementMethod: v })} placeholder={t("step2.workingTime.overtimeSettlementMethodPlaceholder")} options={OVERTIME_SETTLEMENT} />
                                </Field>
                            </Grid2>
                            <SwitchGroup>
                                <FormSwitch label={t("step2.workingTime.overtimeMandatory")} checked={workingTime.overtime.isMandatory} onChange={(v) => setOvertime({ isMandatory: v })} />
                                <FormSwitch label={t("step2.workingTime.refusalIsEliminating")} checked={workingTime.overtime.refusalIsEliminating} onChange={(v) => setOvertime({ refusalIsEliminating: v })} />
                            </SwitchGroup>
                        </Box>
                    )}
                    <AdditionalInfoField value={sectionInfo.workingTime} onChange={(e) => setSectionInfo("workingTime", e.target.value)} />
                </Box>
            </CollapsibleSection>
        </Box>
    );
};

export default Step2Employment;
